import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Shield, Clock, Users, Star, Quote, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";

const Homepage = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  const services = [
    { name: "Tatkal Passport", icon: "⚡" },
    { name: "Fresh Passport", icon: "📄" },
    { name: "ECNR Passport", icon: "🌐" },
    { name: "Passport Renewal", icon: "🔄" }
  ];

  const features = [
    {
      icon: <Clock className="h-12 w-12 text-blue-600" />,
      title: "Quick Processing",
      description: "Get your passport in as fast as 1-2 days with our expedited services"
    },
    {
      icon: <Shield className="h-12 w-12 text-green-600" />,
      title: "Secure & Reliable",
      description: "Trusted by thousands of customers with a 100% success rate"
    },
    {
      icon: <Users className="h-12 w-12 text-purple-600" />,
      title: "Expert Guidance",
      description: "Professional assistance throughout your passport application process"
    }
  ];

  const testimonials = [
    {
      name: "Soumya Bhat",
      text: "Splendid job. Got my passport in 2 days — feeling delighted :)",
      rating: 5,
      location: "Santacruz"
    },
    {
      name: "Rosemaria Paulose", 
      text: "Apply My Passport was of great help… Mr. Sameer was very patient and guided me through the entire process.",
      rating: 5,
      location: "Santacruz"
    },
    {
      name: "Vidisha P",
      text: "Totally recommend Apply My Passport… Got my passport in a day's time. Excellent service!",
      rating: 5,
      location: "Santacruz"
    },
    {
      name: "Ankit Shah",
      text: "Fast, reliable, and stress-free passport application process. Highly recommended.",
      rating: 5,
      location: "Navi Mumbai"
    },
    {
      name: "Priya Menon",
      text: "Outstanding service! The team handled everything professionally and delivered exactly as promised.",
      rating: 5,
      location: "Mankhurd"
    },
    {
      name: "Rajesh Kumar",
      text: "Incredible experience! Got my tatkal passport without any hassle. Staff was extremely helpful.",
      rating: 5,
      location: "Pydhonie"
    },
    {
      name: "Neha Sharma",
      text: "Exceptional service quality. The entire process was smooth and completed ahead of schedule.",
      rating: 5,
      location: "Santacruz"
    },
    {
      name: "Amit Patel",
      text: "Best passport service in Mumbai! Quick, efficient, and hassle-free experience. Highly satisfied.",
      rating: 5,
      location: "Navi Mumbai"
    },
    {
      name: "Kavita Singh",
      text: "Perfect service from start to finish. Got my renewal done in record time. Thank you team!",
      rating: 5,
      location: "Mankhurd"
    }
  ];

  // Auto-rotate testimonials
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-indigo-100 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-4">
              Apply My <span className="text-blue-600">Passport</span>
            </h1>
            
            {/* Prominent Tagline */}
            <div className="mb-6">
              <span className="inline-block bg-green-600 text-white px-6 py-3 rounded-full text-2xl font-bold shadow-lg">
                Get Your Passport in 2 Days*
              </span>
            </div>
            
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Your trusted partner for fast, reliable, and hassle-free passport services. 
              Get your passport in record time with expert guidance every step of the way.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a 
                href="https://wa.me/917977084003" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 text-lg">
                  Apply Now
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </a>
              <Link to="/locations">
                <Button variant="outline" size="lg" className="px-8 py-4 text-lg">
                  Find Location
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Services</h2>
            <p className="text-xl text-gray-600">Comprehensive passport solutions for all your needs</p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <Card key={index} className="text-center p-6 hover:shadow-lg transition-shadow">
                <CardContent className="pt-6">
                  <div className="text-4xl mb-4">{service.icon}</div>
                  <h3 className="text-lg font-semibold text-gray-900">{service.name}</h3>
                </CardContent>
              </Card>
            ))}
          </div>
          
          <div className="text-center mt-10">
            <Link to="/services">
              <Button variant="outline" size="lg">
                View All Services
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Choose Us?</h2>
            <p className="text-xl text-gray-600">Experience the difference with our premium passport services</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="text-center p-8">
                <CardContent className="pt-6">
                  <div className="flex justify-center mb-6">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Client Testimonials Moving Slider */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">What Our Clients Say</h2>
            <p className="text-xl text-gray-600">Hear from thousands of satisfied customers</p>
          </div>
          
          {/* 4 Testimonials Moving Slider */}
          <div className="relative overflow-hidden">
            <div className="flex space-x-6 animate-pulse">
              {/* Display 4 testimonials at once */}
              {testimonials.slice(currentTestimonial, currentTestimonial + 4).concat(
                testimonials.slice(0, Math.max(0, (currentTestimonial + 4) - testimonials.length))
              ).map((testimonial, index) => (
                <Card key={`${currentTestimonial}-${index}`} className="flex-shrink-0 w-80 p-6 hover:shadow-lg transition-all duration-300 border-l-4 border-blue-500">
                  <CardContent className="pt-0">
                    <div className="flex items-center mb-4">
                      <Quote className="h-6 w-6 text-blue-500 mr-2" />
                      <div className="flex text-yellow-400">
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <Star key={i} className="h-6 w-6 fill-current" />
                        ))}
                      </div>
                    </div>
                    <p className="text-gray-600 mb-4 italic text-sm leading-relaxed">"{testimonial.text}"</p>
                    <div className="text-sm">
                      <p className="font-semibold text-gray-900">{testimonial.name}</p>
                      <p className="text-gray-500">{testimonial.location}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            
            {/* Navigation Controls */}
            <div className="flex justify-center mt-8 space-x-4">
              <button
                onClick={prevTestimonial}
                className="bg-blue-600 hover:bg-blue-700 text-white p-3 rounded-full transition-colors"
                aria-label="Previous testimonials"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              
              <button
                onClick={nextTestimonial}
                className="bg-blue-600 hover:bg-blue-700 text-white p-3 rounded-full transition-colors"
                aria-label="Next testimonials"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
            
            {/* Pagination Dots */}
            <div className="flex justify-center mt-6 space-x-2">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTestimonial(index)}
                  className={`w-3 h-3 rounded-full transition-colors ${
                    Math.floor(index / 4) === Math.floor(currentTestimonial / 4) ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                  aria-label={`Go to testimonial set ${Math.floor(index / 4) + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Ready to Apply?</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Join thousands of satisfied customers who got their passports quickly and hassle-free
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="https://wa.me/917977084003" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              <Button size="lg" variant="secondary" className="px-8 py-4 text-lg">
                Apply Now
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </a>
            <a href="tel:+917977084003">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-blue-600 px-8 py-4 text-lg">
                Call Now
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Homepage;